import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import type { CommunityDesignItem } from "@/types/api";
import { getDesignPreviewUrl } from "@/lib/imagePrefetch";
import { formatNumber, timeAgo } from "@/lib/utils";

export function BlueprintGrid({ light = false }: { light?: boolean }) {
  return <div className={light ? "bp-grid bp-grid-light" : "bp-grid"} aria-hidden="true" />;
}

export function CoordLabel({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <span className={`bp-coord ${className}`}>{children}</span>;
}

export function BpFrame({
  children,
  className = "",
  label,
}: {
  children: ReactNode;
  className?: string;
  label?: string;
}) {
  return (
    <div className={`bp-frame ${className}`}>
      {label ? <span className="bp-frame-label">{label}</span> : null}
      {children}
    </div>
  );
}

export function BlueprintCanvas() {
  return (
    <div className="bp-canvasEl" aria-hidden="true">
      <div className="bp-measure bp-measure-x" />
      <div className="bp-measure bp-measure-y" />
      <div className="bp-circle bp-circle-a" />
      <div className="bp-circle bp-circle-b" />
      <div className="bp-blueprint-card bp-card-a">
        <BlueprintArt />
      </div>
      <div className="bp-blueprint-card bp-card-b">
        <div className="bp-mini-bars">
          <span />
          <span />
          <span />
          <span />
        </div>
      </div>
      <div className="bp-redline" />
      <CoordLabel className="bp-canvasEl-label-a">X 042.INK</CoordLabel>
      <CoordLabel className="bp-canvasEl-label-b">Y 118.POP</CoordLabel>
    </div>
  );
}

export function InteractiveBlueprintCanvas() {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const canvasEl = canvas;
    const drawingContext = ctx;

    let width = 0;
    let height = 0;
    let animationId = 0;
    let tick = 0;
    let phase = 0;
    let phaseTimer = 0;
    let ambientTimer = 0;
    let popIndex = 0;

    const blueprintLine = "rgba(190,218,242,";
    const pop = ["rgba(6,73,119,", "rgba(22,136,216,", "rgba(47,159,232,", "rgba(118,196,246,"];
    const popColor = (index: number): string => pop[((index % pop.length) + pop.length) % pop.length] ?? "rgba(22,136,216,";
    const drawnLines: DrawnLine[] = [];
    const splashes: PopSplash[] = [];

    function resize() {
      const rect = canvasEl.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      canvasEl.width = Math.floor(width * dpr);
      canvasEl.height = Math.floor(height * dpr);
      drawingContext.setTransform(dpr, 0, 0, dpr, 0, 0);
      drawingContext.clearRect(0, 0, width, height);
      drawingContext.fillRect(0, 0, width, height);
    }

    class DrawnLine {
      pts: { x: number; y: number }[];
      color: string;
      lineWidth: number;
      delay: number;
      duration: number;
      t = 0;
      started = false;
      done = false;
      alpha = 0.62;
      age = 0;
      maxAge: number;

      constructor(pts: { x: number; y: number }[], color: string, lineWidth = 1, delay = 0, duration = 60) {
        this.pts = pts;
        this.color = color;
        this.lineWidth = lineWidth;
        this.delay = delay;
        this.duration = duration;
        this.maxAge = duration + 420;
      }

      update() {
        this.age += 1;
        if (this.age < this.delay) return;
        if (!this.started) this.started = true;
        if (this.t < this.duration) this.t += 1;
        if (this.age > this.maxAge) {
          this.alpha -= 0.003;
          if (this.alpha <= 0) this.done = true;
        }
      }

      draw() {
        if (!this.started || this.pts.length < 2) return;
        const progress = Math.min(1, this.t / this.duration);
        const endIndex = Math.floor(progress * (this.pts.length - 1));
        if (endIndex < 1) return;

        drawingContext.beginPath();
        const firstPoint = this.pts[0];
        if (!firstPoint) return;
        drawingContext.moveTo(firstPoint.x, firstPoint.y);
        for (let i = 1; i <= endIndex; i += 1) {
          const point = this.pts[i];
          if (point) drawingContext.lineTo(point.x, point.y);
        }
        drawingContext.strokeStyle = `${this.color}${this.alpha})`;
        drawingContext.lineWidth = this.lineWidth;
        drawingContext.lineCap = "round";
        drawingContext.lineJoin = "round";
        drawingContext.stroke();
      }
    }

    class PopSplash {
      x: number;
      y: number;
      color: string;
      radius = 0;
      maxRadius: number;
      alpha = 0.72;
      speed: number;
      done = false;
      age = 0;
      points: { angle: number; radius: number }[];

      constructor(x: number, y: number, color: string) {
        this.x = x;
        this.y = y;
        this.color = color;
        this.maxRadius = 60 + Math.random() * 80;
        this.speed = 2.5 + Math.random();
        this.points = Array.from({ length: 12 }, (_, i) => ({
          angle: (i / 12) * Math.PI * 2,
          radius: this.maxRadius * (0.7 + Math.random() * 0.6),
        }));
      }

      update() {
        this.age += 1;
        this.radius = Math.min(this.radius + this.speed, this.maxRadius);
        if (this.age > 120) {
          this.alpha -= 0.007;
          if (this.alpha <= 0) this.done = true;
        }
      }

      draw() {
        const progress = this.radius / this.maxRadius;
        drawingContext.beginPath();
        this.points.forEach((point, index) => {
          const radius = point.radius * progress;
          const x = this.x + Math.cos(point.angle) * radius;
          const y = this.y + Math.sin(point.angle) * radius;
          if (index === 0) drawingContext.moveTo(x, y);
          else drawingContext.lineTo(x, y);
        });
        drawingContext.closePath();
        drawingContext.fillStyle = `${this.color}${this.alpha * 0.25})`;
        drawingContext.fill();
        drawingContext.strokeStyle = `${this.color}${this.alpha * 0.6})`;
        drawingContext.lineWidth = 1.5;
        drawingContext.stroke();

        if (progress < 0.8) {
          drawingContext.beginPath();
          drawingContext.arc(this.x, this.y, 5 * (1 - progress), 0, Math.PI * 2);
          drawingContext.fillStyle = `${this.color}${this.alpha * 0.8})`;
          drawingContext.fill();
        }
      }
    }

    class Quill {
      x = -100;
      y = -100;
      targetX = 0;
      targetY = 0;
      rotation = -0.2;
      alpha = 0;
      length = 90;

      moveTo(x: number, y: number) {
        this.targetX = x;
        this.targetY = y;
      }

      update() {
        this.x += (this.targetX - this.x) * 0.025;
        this.y += (this.targetY - this.y) * 0.025;
        this.alpha += (0.9 - this.alpha) * 0.04;
        const dx = this.targetX - this.x;
        const dy = this.targetY - this.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) {
          this.rotation += (Math.atan2(dx, -dy) * 0.12 - this.rotation) * 0.06;
        }
      }

      draw() {
        const { x, y, rotation, alpha, length } = this;
        drawingContext.save();
        drawingContext.translate(x, y);
        drawingContext.rotate(rotation);
        drawingContext.globalAlpha = alpha;

        drawingContext.strokeStyle = "rgba(22,136,216,1)";
        drawingContext.lineWidth = length * 0.016;
        drawingContext.lineCap = "round";
        drawingContext.beginPath();
        drawingContext.moveTo(0, -length * 0.85);
        drawingContext.quadraticCurveTo(length * 0.03, 0, 0, length * 0.85);
        drawingContext.stroke();

        for (let i = -16; i <= 16; i += 1) {
          const t = i / 16;
          const by = t * length * 0.72;
          const bx = t * length * 0.02;
          const barbLength = length * 0.26 * (1 - Math.abs(t) * 0.5);
          if (barbLength < 2) continue;
          for (const side of [-1, 1]) {
            drawingContext.beginPath();
            drawingContext.moveTo(bx, by);
            drawingContext.quadraticCurveTo(
              bx + side * Math.sin(0.38) * barbLength * 0.55,
              by - Math.cos(0.38) * barbLength * 0.25,
              bx + side * Math.sin(0.38) * barbLength,
              by - Math.cos(0.38) * barbLength
            );
            drawingContext.strokeStyle = `rgba(22,136,216,${alpha * (0.35 + (1 - Math.abs(t)) * 0.45)})`;
            drawingContext.lineWidth = length * 0.006 * (1 - Math.abs(t) * 0.4);
            drawingContext.stroke();
          }
        }

        drawingContext.beginPath();
        drawingContext.moveTo(-length * 0.02, -length * 0.85);
        drawingContext.lineTo(0, -length * 0.98);
        drawingContext.lineTo(length * 0.02, -length * 0.85);
        drawingContext.strokeStyle = `rgba(190,218,242,${alpha * 0.9})`;
        drawingContext.lineWidth = 0.8;
        drawingContext.stroke();
        drawingContext.restore();
      }
    }

    const quill = new Quill();

    function makeBlueprintBurst(cx: number, cy: number) {
      for (let i = 0; i < 20; i += 1) {
        const angle = (i / 20) * Math.PI * 2;
        const r1 = 20;
        const r2 = 100 + Math.random() * 40;
        const pts = Array.from({ length: 20 }, (_, t) => ({
          x: cx + Math.cos(angle) * (r1 + (t / 19) * (r2 - r1)),
          y: cy + Math.sin(angle) * (r1 + (t / 19) * (r2 - r1)),
        }));
        drawnLines.push(new DrawnLine(pts, blueprintLine, i % 3 === 0 ? 1.2 : 0.6, i * 12, 40));
      }

      [30, 60, 90, 115].forEach((radius, i) => {
        const pts = Array.from({ length: 60 }, (_, t) => ({
          x: cx + Math.cos((t / 59) * Math.PI * 2) * radius,
          y: cy + Math.sin((t / 59) * Math.PI * 2) * radius,
        }));
        drawnLines.push(new DrawnLine(pts, blueprintLine, i === 0 ? 1.5 : 0.7, 200 + i * 80, 90));
      });
    }

    function makeChevronPattern(cx: number, cy: number) {
      const patternWidth = Math.min(width * 0.5, 400);
      for (let row = 0; row < 6; row += 1) {
        const y = cy - 60 + row * 24;
        const pts = Array.from({ length: 40 }, (_, index) => {
          const t = index / 39;
          return {
            x: cx - patternWidth / 2 + t * patternWidth,
            y: y + Math.abs((t - 0.5) * 40) - 20,
          };
        });
        drawnLines.push(new DrawnLine(pts, row === 2 || row === 3 ? popColor(1) : blueprintLine, row === 2 || row === 3 ? 1.5 : 0.8, row * 80, 80));
      }
    }

    function makeGridPattern(cx: number, cy: number) {
      const size = 120;
      for (let i = -3; i <= 3; i += 1) {
        drawnLines.push(new DrawnLine([{ x: cx - size, y: cy + i * size / 3 }, { x: cx + size, y: cy + i * size / 3 }], blueprintLine, 0.7, (i + 3) * 30, 50));
        drawnLines.push(new DrawnLine([{ x: cx + i * size / 3, y: cy - size }, { x: cx + i * size / 3, y: cy + size }], blueprintLine, 0.7, (i + 3) * 30 + 15, 50));
      }
    }

    function makeDimensionLines(cx: number, cy: number) {
      const patternWidth = Math.min(width * 0.55, 450);
      const patternHeight = 160;
      const x1 = cx - patternWidth / 2;
      const x2 = cx + patternWidth / 2;
      const y1 = cy - patternHeight / 2;
      const y2 = cy + patternHeight / 2;
      [[{ x: x1, y: y1 }, { x: x2, y: y1 }], [{ x: x2, y: y1 }, { x: x2, y: y2 }], [{ x: x2, y: y2 }, { x: x1, y: y2 }], [{ x: x1, y: y2 }, { x: x1, y: y1 }]].forEach((pts, i) => {
        drawnLines.push(new DrawnLine(pts, blueprintLine, 1.5, i * 50, 60));
      });
      drawnLines.push(new DrawnLine([{ x: cx - (patternWidth + 40) / 2, y: y1 - 20 }, { x: cx + (patternWidth + 40) / 2, y: y1 - 20 }], blueprintLine, 0.8, 250, 60));
      drawnLines.push(new DrawnLine([{ x: x1, y: y2 + 20 }, { x: x1, y: y1 - 25 }], blueprintLine, 0.8, 280, 40));
      drawnLines.push(new DrawnLine([{ x: x2, y: y2 + 20 }, { x: x2, y: y1 - 25 }], blueprintLine, 0.8, 290, 40));
    }

    const phases = [
      () => {
        quill.moveTo(width * 0.5, height * 0.45);
        makeBlueprintBurst(width * 0.5, height * 0.45);
        window.setTimeout(() => splashes.push(new PopSplash(width * 0.5, height * 0.45, popColor(popIndex++))), 300);
      },
      () => {
        quill.moveTo(width * 0.5, height * 0.7);
        makeChevronPattern(width * 0.5, height * 0.7);
        window.setTimeout(() => splashes.push(new PopSplash(width * 0.5, height * 0.7, popColor(popIndex++))), 400);
      },
      () => {
        quill.moveTo(width * 0.5, height * 0.3);
        makeGridPattern(width * 0.5, height * 0.3);
        window.setTimeout(() => {
          splashes.push(new PopSplash(width * 0.3, height * 0.3, popColor(popIndex++)));
          splashes.push(new PopSplash(width * 0.7, height * 0.3, popColor(popIndex++)));
        }, 500);
      },
      () => {
        quill.moveTo(width * 0.5, height * 0.5);
        makeDimensionLines(width * 0.5, height * 0.5);
        window.setTimeout(() => splashes.push(new PopSplash(width * 0.5, height * 0.5, popColor(popIndex++))), 600);
      },
    ];

    function frame() {
      animationId = requestAnimationFrame(frame);
      tick += 1;
      drawingContext.clearRect(0, 0, width, height);

      quill.update();
      phaseTimer += 1;
      if (phaseTimer === 60 && phase === 0) phases[0]?.();
      if (phaseTimer === 500) {
        phase = (phase + 1) % phases.length;
        phaseTimer = 0;
        phases[phase]?.();
      }

      ambientTimer += 1;
      if (ambientTimer > 180 + Math.random() * 120) {
        ambientTimer = 0;
        const x = width * (0.16 + Math.random() * 0.68);
        const y = height * (0.18 + Math.random() * 0.64);
        if (Math.random() > 0.45) {
          splashes.push(new PopSplash(x, y, popColor(popIndex++)));
        }
        const rays = 5 + Math.floor(Math.random() * 5);
        for (let i = 0; i < rays; i += 1) {
          const angle = (i / rays) * Math.PI * 2 + Math.random() * 0.3;
          const length = 42 + Math.random() * 64;
          drawnLines.push(new DrawnLine([{ x, y }, { x: x + Math.cos(angle) * length, y: y + Math.sin(angle) * length }], Math.random() > 0.5 ? blueprintLine : popColor(popIndex + i), 0.55, i * 10, 34));
        }
      }

      for (let i = drawnLines.length - 1; i >= 0; i -= 1) {
        const line = drawnLines[i];
        if (!line) continue;
        line.update();
        line.draw();
        if (line.done) drawnLines.splice(i, 1);
      }

      for (let i = splashes.length - 1; i >= 0; i -= 1) {
        const splash = splashes[i];
        if (!splash) continue;
        splash.update();
        splash.draw();
        if (splash.done) splashes.splice(i, 1);
      }

      quill.draw();

      drawingContext.globalAlpha = 1;
    }

    function handlePointerDown(event: PointerEvent) {
      const rect = canvasEl.getBoundingClientRect();
      if (
        event.clientX < rect.left ||
        event.clientX > rect.right ||
        event.clientY < rect.top ||
        event.clientY > rect.bottom
      ) {
        return;
      }
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      splashes.push(new PopSplash(x, y, popColor(popIndex++)));
      quill.moveTo(x, y);
      for (let ring = 0; ring < 2; ring += 1) {
        const rays = ring === 0 ? 12 : 18;
        for (let i = 0; i < rays; i += 1) {
          const angle = (i / rays) * Math.PI * 2 + ring * 0.11;
          const length = ring === 0 ? 80 : 125;
          drawnLines.push(new DrawnLine([{ x, y }, { x: x + Math.cos(angle) * length, y: y + Math.sin(angle) * length }], popColor(popIndex + i + ring), ring === 0 ? 0.95 : 0.55, i * 5, ring === 0 ? 28 : 42));
        }
      }
    }

    resize();
    frame();
    window.addEventListener("resize", resize);
    window.addEventListener("pointerdown", handlePointerDown, { capture: true });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointerdown", handlePointerDown, { capture: true });
    };
  }, []);

  return <canvas ref={ref} className="bp-interactive-canvas" aria-label="Interactive blueprint drawing surface" />;
}

export function BlueprintArt() {
  return (
    <svg viewBox="0 0 240 260" role="img" aria-label="Blueprint artwork preview">
      <rect x="22" y="22" width="196" height="216" rx="4" fill="none" stroke="currentColor" strokeWidth="3" />
      <path d="M61 77c35-42 82-39 116 0" fill="none" stroke="currentColor" strokeWidth="3" />
      <path d="M55 171c38 26 89 27 130 0" fill="none" stroke="currentColor" strokeWidth="3" />
      <circle cx="82" cy="122" r="17" fill="none" stroke="currentColor" strokeWidth="3" />
      <circle cx="157" cy="122" r="17" fill="none" stroke="currentColor" strokeWidth="3" />
      <path d="M101 150c14 9 27 9 39 0" fill="none" stroke="currentColor" strokeWidth="4" />
      <path d="M42 45h156M42 215h156M120 22v216" stroke="currentColor" strokeWidth="1.5" opacity="0.35" />
    </svg>
  );
}

export function BlueprintButton({
  children,
  href,
  onClick,
  variant = "primary",
}: {
  children: ReactNode;
  href?: string;
  onClick?: () => void;
  variant?: "primary" | "ghost";
}) {
  const className = variant === "primary" ? "bp-button bp-button-primary" : "bp-button bp-button-ghost";
  if (href) {
    return (
      <a className={className} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel={href.startsWith("http") ? "noopener noreferrer" : undefined}>
        {children}
      </a>
    );
  }
  return (
    <button className={className} onClick={onClick} type="button">
      {children}
    </button>
  );
}

export function DesignPlate({
  design,
  active = false,
  onSelect,
}: {
  design: CommunityDesignItem;
  active?: boolean;
  onSelect?: () => void;
}) {
  const image = getDesignPreviewUrl(design);
  const creator = design.creator_display_name || "Quuil maker";
  const title = design.title || "Untitled drop";
  const label = getDesignLabel(design);

  return (
    <button
      type="button"
      className={`bp-design-plate ${active ? "is-active" : ""}`}
      onClick={onSelect}
      aria-label={`Open ${title}`}
    >
      <span className="bp-design-tag">{label}</span>
      <span className="bp-design-image-wrap">
        {image ? <img src={image} alt="" loading="lazy" /> : <BlueprintArt />}
      </span>
      <span className="bp-design-meta">
        <strong>{title}</strong>
        <span>{creator}</span>
      </span>
      <span className="bp-design-stats">
        <span>{formatNumber(design.save_count)} saves</span>
        <span>{timeAgo(design.created_at)}</span>
      </span>
    </button>
  );
}

function getDesignLabel(design: CommunityDesignItem) {
  const realTag = design.tags?.find((tag) => tag.trim().length > 0);
  return realTag || design.style_name || "Inkwell";
}

export function BlueprintLoading({ label = "Loading the Inkwell" }: { label?: string }) {
  return (
    <div className="bp-empty-state" role="status">
      <span className="bp-spinner" />
      <span>{label}</span>
    </div>
  );
}

export function BlueprintEmpty({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="bp-empty-state">
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}
